/**
 * Подключение к чату платформы (`/ws/chat`) — одно на всех, кто в этот чат ходит (PROP-383).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Собеседников с сервером стало двое: страница чат-бота и правая панель
 * агента, доступная на любом экране. Протокол у них общий — кадр авторизации, `participants`,
 * `message`, переподключение, — и написанный дважды он разойдётся молча: сервер добавит поле,
 * поправят одного клиента, второй продолжит работать «почти правильно». Это тот же класс, что
 * два органа управления одним решением (PROP-329) — расходятся всегда, и всегда незаметно.
 *
 * Здесь ТОЛЬКО транспорт: адрес, авторизация, переподключение и разбор конверта. Что делать с
 * пришедшим сообщением — дело вызывающего: у страницы и у панели показ разный, и сводить его
 * сюда значило бы построить третий экран внутри сервиса.
 */
import { getConfig } from '@/libs/axios'

export const MAX_RECONNECT_ATTEMPTS = 10
export const RECONNECT_INTERVAL = 3000

export function chatWsUrl() {
  const url = new URL('/ws/chat', getConfig())
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

/**
 * Открыть соединение.
 *
 * @param {object} opts
 * @param {function} opts.token     как достать токен ПРЯМО СЕЙЧАС (не значение: сессия
 *                                  переоткрывается, и токен, взятый при создании, протухает)
 * @param {function} opts.onStatus  'connecting' | 'online' | 'offline'
 * @param {function} opts.onMessage разобранный JSON-конверт сервера
 * @returns {{send: function, close: function, isOpen: function}}
 */
export function openChatSocket({ token, onStatus, onMessage }) {
  let ws = null
  let attempts = 0
  let timer = null
  let closedByUs = false

  const status = s => { if (onStatus) onStatus(s) }

  function close(byUs = true) {
    clearTimeout(timer)
    if (byUs) closedByUs = true
    if (ws) {
      ws.onclose = null // иначе собственное закрытие запустит переподключение
      ws.onerror = null
      try { ws.close() } catch (e) { /* уже закрыт */ }
      ws = null
    }
  }

  function connect() {
    close(false)
    closedByUs = false
    status('connecting')
    ws = new WebSocket(chatWsUrl())

    ws.onopen = () => {
      attempts = 0
      status('online')
      // Первый кадр — всегда авторизация: сервер до неё не знает, чья это сессия, и любое
      // другое сообщение отбросит.
      ws.send(JSON.stringify({ type: 'auth', token: token() }))
    }

    ws.onmessage = event => {
      let data = null
      try {
        data = JSON.parse(event.data)
      } catch (e) {
        return // не JSON — не наш кадр
      }
      if (onMessage) onMessage(data)
    }

    // Переподключение живёт прямо здесь, а не отдельной функцией: вынесенная, она ссылалась бы
    // на `connect`, а `connect` — на неё, и такой круг линтер разрывать не умеет.
    ws.onclose = () => {
      status('offline')
      if (closedByUs || attempts >= MAX_RECONNECT_ATTEMPTS) return
      attempts += 1
      clearTimeout(timer)
      timer = setTimeout(connect, RECONNECT_INTERVAL)
    }

    ws.onerror = () => { status('offline') }
  }

  function send(payload) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify(payload))
    return true
  }

  connect()
  return {
    send,
    close: () => close(true),
    isOpen: () => !!ws && ws.readyState === WebSocket.OPEN,
  }
}
